import PropTypes from 'prop-types';
import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from '../../../components/Iconify';

const KYC_ITEMS = [
  { key: 'selfie_verified', label: 'Selfie', icon: 'solar:camera-bold' },
  { key: 'identity_verified', label: 'Identity', icon: 'solar:card-bold' },
  { key: 'bvn_verified', label: 'BVN', icon: 'solar:shield-keyhole-bold' },
  { key: 'address_verified', label: 'Address', icon: 'solar:map-point-bold' },
];

function KycStatusRow({ icon, label, verified }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            color: verified ? 'primary.main' : 'text.primary',
            bgcolor: verified ? (theme) => alpha(theme.palette.primary.main, 0.12) : 'grey.100',
          }}
        >
          <Iconify icon={icon} width={14} height={14} />
        </Box>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Stack>

      <Typography variant="caption" color={verified ? 'primary.main' : 'text.secondary'} sx={{ fontWeight: 600 }}>
        {verified ? 'Verified' : 'Pending'}
      </Typography>
    </Stack>
  );
}

KycStatusRow.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  verified: PropTypes.bool.isRequired,
};

export default function KycSummaryCard({ kycStatus }) {
  const resolvedStatus = kycStatus || {};
  const tier = resolvedStatus.kyc_tier || 'unknown';
  const checks = KYC_ITEMS.map((item) => Boolean(resolvedStatus[item.key]));
  const isVerified = checks.length > 0 && checks.every(Boolean);
  const hasAnyVerification = checks.some(Boolean);
  const summaryState = !kycStatus ? 'loading' : isVerified ? 'verified' : 'unverified';
  const summaryCopy =
    summaryState === 'loading'
      ? 'Loading verification state'
      : summaryState === 'verified'
        ? 'All verification checks completed'
        : hasAnyVerification
          ? 'Verification is partially completed'
          : 'No verification checks completed';

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.5,
        mx: 0.75,
        mt: 1,
        mb: 0.5,
        borderRadius: 1.75,
        bgcolor: 'background.paper',
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2">KYC Summary</Typography>
          {summaryState === 'loading' ? (
            <Stack spacing={0.75} sx={{ mt: 0.75 }}>
              <Skeleton width={110} height={16} />
              <Skeleton width={140} height={14} />
            </Stack>
          ) : (
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: summaryState === 'verified' ? 'primary.main' : 'text.secondary',
                  fontWeight: 700,
                  textTransform: 'capitalize',
                }}
              >
                {summaryState}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tier: {tier}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summaryCopy}
              </Typography>
            </Stack>
          )}
        </Box>

        <Divider />

        <Stack spacing={1.1}>
          {summaryState === 'loading'
            ? KYC_ITEMS.map((item) => (
                <Stack key={item.key} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="rounded" width={24} height={24} />
                    <Skeleton width={70} height={16} />
                  </Stack>
                  <Skeleton width={46} height={14} />
                </Stack>
              ))
            : KYC_ITEMS.map((item) => (
                <KycStatusRow
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  verified={Boolean(resolvedStatus[item.key])}
                />
              ))}
        </Stack>
      </Stack>
    </Box>
  );
}

KycSummaryCard.propTypes = {
  kycStatus: PropTypes.object,
};
